import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play } from "lucide-react";
import { hero, cta, media } from "../../content/siteContent";
import { CTAButton } from "./primitives";
import { LineReveal } from "../motion/Reveal";
import { FilmModal } from "./FilmModal";
import { scrollToId } from "../../hooks/useLenis";
import { track, EVENTS } from "../../lib/analytics";

const SCENE_MS = 3400;
const scenes = media.heroScenes || [];

/* Small crisp "Daily Operating Report" status chip (composited, not baked in). */
const OperatingReport = () => (
  <div className="pointer-events-none select-none w-[230px] rounded-sm border border-[rgba(246,248,250,0.18)] bg-[#0C1B2E]/70 backdrop-blur-md p-4">
    <div className="flex items-center justify-between border-b border-hair-dark pb-2 mb-2">
      <span className="text-[9px] tracking-[0.18em] text-white/60 font-display font-semibold">MERIDIAN LITHIUM · DAILY REPORT</span>
      <span className="h-1.5 w-1.5 rounded-full bg-[#e05c5c] animate-pulse" />
    </div>
    {[
      ["Tonnes vs plan", "-8%", "bad"],
      ["Unplanned downtime", "▲ high", "bad"],
      ["Asset availability", "below", "warn"],
      ["Cost per tonne", "adverse", "bad"],
    ].map(([k, v, s]) => (
      <div key={k} className="flex items-center justify-between py-[3px]">
        <span className="text-[10px] text-white/55">{k}</span>
        <span className={`text-[10px] font-display font-semibold ${s === "bad" ? "text-[#e97070]" : "text-[#d9b25a]"}`}>{v}</span>
      </div>
    ))}
  </div>
);

/* Value bridge chip for the payoff scene. */
const ValueBridge = () => (
  <div className="pointer-events-none select-none rounded-sm border border-teal/40 bg-[#0C1B2E]/70 backdrop-blur-md px-4 py-3">
    <span className="text-[9px] tracking-[0.18em] text-teal font-display font-semibold">TRANSFORMATION VALUE BRIDGE</span>
    <div className="mt-2 flex items-center gap-1.5">
      {["Opportunity", "Validated", "Implemented", "Banked"].map((s, i) => (
        <React.Fragment key={s}>
          <span className={`text-[9px] font-display font-semibold ${i === 3 ? "text-gold" : "text-white/70"}`}>{s}</span>
          {i < 3 && <span className="text-teal text-[10px]">›</span>}
        </React.Fragment>
      ))}
    </div>
  </div>
);

export const HeroCinematic = () => {
  const [idx, setIdx] = useState(0);
  const [reduce, setReduce] = useState(false);
  const [filmOpen, setFilmOpen] = useState(false);
  const timer = useRef(null);
  const hasVideo = Boolean(media.heroVideo);
  const hasFilm = Boolean(media.heroFilmUrl);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduce(mq.matches);
    // preload
    scenes.forEach((s) => { const i = new Image(); i.src = s.url; });
  }, []);

  useEffect(() => {
    if (reduce || hasVideo || scenes.length === 0) return;
    timer.current = setInterval(() => setIdx((i) => (i + 1) % scenes.length), SCENE_MS);
    return () => clearInterval(timer.current);
  }, [reduce, hasVideo]);

  const primary = () => { track(EVENTS.HERO_CTA, { target: "prospectus" }); scrollToId("prospectus"); };
  const secondary = () => { track(EVENTS.HERO_CTA, { target: "system" }); scrollToId("system"); };

  const activeScene = scenes[idx] || {};

  return (
    <section id="top" data-testid="hero-section" className="relative min-h-[100svh] flex items-end overflow-hidden bg-navy">
      {/* Background layer */}
      <div className="absolute inset-0 z-0">
        {hasVideo ? (
          <video className="h-full w-full object-cover" autoPlay muted loop playsInline poster={media.heroPoster}>
            <source src={media.heroVideo} type="video/mp4" />
          </video>
        ) : reduce ? (
          <img src={media.heroPoster} alt="Australian open-cut mine operating smoothly at dawn" className="h-full w-full object-cover" />
        ) : (
          scenes.map((s, i) => (
            <div
              key={s.url}
              className="absolute inset-0 transition-opacity duration-[1400ms] ease-out"
              style={{ opacity: i === idx ? 1 : 0 }}
            >
              <img
                src={s.url}
                alt=""
                aria-hidden="true"
                className="h-full w-full object-cover"
                style={i === idx ? { animation: `kenburns ${SCENE_MS + 1600}ms ease-out both` } : undefined}
              />
            </div>
          ))
        )}
      </div>

      {/* Overlays for legibility */}
      <div className="absolute inset-0 z-[1] bg-[#0C1B2E]/55" />
      <div className="absolute inset-0 z-[1] bg-gradient-to-r from-[#0C1B2E] via-[#0C1B2E]/55 to-transparent" />
      <div className="absolute inset-0 z-[1] bg-gradient-to-t from-[#0C1B2E] via-transparent to-[#0C1B2E]/40" />
      <div className="absolute inset-0 z-[1] blueprint opacity-40" />

      {/* Composited artefacts (desktop only) */}
      {!reduce && !hasVideo && (
        <div className="hidden lg:block absolute top-28 right-12 z-[2]">
          <AnimatePresence>
            {idx <= 2 && (
              <motion.div key="report" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.6 }}>
                <OperatingReport />
              </motion.div>
            )}
            {idx === scenes.length - 1 && (
              <motion.div key="bridge" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.6 }}>
                <ValueBridge />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}

      {/* Content */}
      <div className="container-page pb-16 pt-32 w-full relative z-10">
        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1, duration: 0.8 }}
          className="eyebrow text-teal max-w-2xl mb-6"
        >
          {hero.eyebrow}
        </motion.p>

        <h1 className="font-display font-extrabold tracking-tight text-white text-4xl sm:text-6xl lg:text-[4.25rem] leading-[1.03] max-w-4xl">
          <LineReveal lines={hero.headlineLines} delay={0.25} />
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.95, duration: 0.8 }}
          className="mt-7 text-white/80 text-base sm:text-lg leading-relaxed max-w-xl"
        >
          {hero.supporting}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.8 }}
          className="mt-9 flex flex-col sm:flex-row items-stretch sm:items-center gap-4"
        >
          <CTAButton data-testid="hero-prospectus-cta" onClick={primary} withArrow>{cta.prospectus}</CTAButton>
          <CTAButton data-testid="hero-explore-cta" variant="outlineLight" onClick={secondary}>{cta.exploreSystem}</CTAButton>
        </motion.div>

        {/* Prominent video poster card */}
        {hasFilm && (
          <motion.button
            data-testid="hero-watch-film"
            onClick={() => { track(EVENTS.HERO_CTA, { target: "film" }); setFilmOpen(true); }}
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.25, duration: 0.8 }}
            className="group mt-8 block w-full max-w-sm text-left focus-ring rounded-sm"
            aria-label="Watch the film (3 minutes)"
          >
            <div className="relative aspect-video rounded-sm overflow-hidden border border-white/25 shadow-2xl">
              <img
                src={media.heroFilmPoster}
                alt=""
                aria-hidden="true"
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-[#0C1B2E]/45 group-hover:bg-[#0C1B2E]/25 transition-colors duration-300" />
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="relative flex h-16 w-16 items-center justify-center">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-teal/60 hero-play-pulse" />
                  <span className="relative inline-flex h-16 w-16 items-center justify-center rounded-full bg-teal text-white shadow-lg group-hover:bg-[#08929f] transition-colors duration-300">
                    <Play className="h-6 w-6 fill-current ml-0.5" />
                  </span>
                </span>
              </div>
              <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between px-4 py-3 bg-gradient-to-t from-[#0C1B2E] via-[#0C1B2E]/70 to-transparent">
                <span className="text-white font-display font-semibold text-sm tracking-tight">Watch the film</span>
                <span className="text-white/75 text-xs font-display tracking-wide">3 MIN · SOUND ON</span>
              </div>
            </div>
          </motion.button>
        )}

        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.35, duration: 0.8 }}
          className="mt-8 text-teal/90 text-xs sm:text-sm font-medium tracking-wide">
          {hero.proofLine}
        </motion.p>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.5, duration: 0.8 }}
          className="mt-4 text-white/45 text-xs leading-relaxed max-w-2xl">
          {hero.qualificationNote}
        </motion.p>

        {/* Scene label + progress */}
        {!reduce && !hasVideo && scenes.length > 0 && (
          <div className="mt-10 flex items-center gap-5" data-testid="hero-scene">
            <div className="flex gap-1.5">
              {scenes.map((_, i) => (
                <span key={i} className="relative h-[3px] w-7 bg-white/20 overflow-hidden rounded-full">
                  <span
                    className="absolute inset-y-0 left-0 bg-teal rounded-full"
                    style={{
                      width: i < idx ? "100%" : i === idx ? "100%" : "0%",
                      transition: i === idx ? `width ${SCENE_MS}ms linear` : "width 0.3s",
                    }}
                  />
                </span>
              ))}
            </div>
            <AnimatePresence mode="wait">
              <motion.span
                key={idx}
                initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.5 }}
                className="eyebrow text-white/75 text-[10px]"
              >
                {activeScene.label}
              </motion.span>
            </AnimatePresence>
          </div>
        )}
      </div>

      <style>{`@keyframes kenburns { from { transform: scale(1.02) translate3d(0,0,0);} to { transform: scale(1.12) translate3d(-1.5%,-1.5%,0);} }
      @keyframes heroPlayPulse { 0% { transform: scale(1); opacity: 0.6; } 70%, 100% { transform: scale(1.9); opacity: 0; } }
      .hero-play-pulse { animation: heroPlayPulse 2.2s cubic-bezier(0,0,0.2,1) infinite; }
      @media (prefers-reduced-motion: reduce) { .hero-play-pulse { animation: none; } }`}</style>
      <FilmModal open={filmOpen} onClose={() => setFilmOpen(false)} src={media.heroFilmUrl} />
    </section>
  );
};
